import { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { FiEye, FiDownload, FiTrash2 } from "react-icons/fi";


const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const AssignmentDocumentsTab = ({ assignmentId }) => {

  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentName, setDocumentName] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (assignmentId) {
      loadDocumentTypes();
      loadDocuments();
    }
  }, [assignmentId]);

  // =====================================================
  // LOAD DOCUMENT TYPES
  // =====================================================
  const loadDocumentTypes = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/assignments/document-types`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setDocumentTypes(
        res.data.map(type => ({
          value: type.DocumentTypeID,
          label: type.TypeName
        }))
      );

    } catch (err) {
      console.error("Error loading document types", err);
    }
  };

  // =====================================================
  // LOAD DOCUMENT LIST
  // =====================================================
  const loadDocuments = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/assignments/${assignmentId}/documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setDocuments(res.data);

    } catch (err) {
      console.error("Error loading documents", err);
    }
  };

  // =====================================================
  // UPLOAD
  // =====================================================
  const handleUpload = async () => {
    if (!selectedType || !file) {
      alert("Select document type and file");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentTypeId", selectedType.value);
      formData.append("documentName", documentName || file.name);

      await axios.post(
        `${API_URL}/api/assignments/${assignmentId}/documents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setDocumentName("");
      setFile(null);
      setSelectedType(null);

      loadDocuments();

    } catch (err) {
      console.error("Upload error", err.response?.data || err);
      alert("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // DOWNLOAD
  // =====================================================
  const handleDownload = async (documentId, fileName) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/assignments/documents/${documentId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

    } catch (err) {
      console.error("Download error:", err);
      alert("Download failed");
    }
  };

  // =====================================================
  // PREVIEW
  // =====================================================
  const handlePreview = async (documentId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/assignments/documents/${documentId}/download?preview=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );

      const fileURL = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );

      window.open(fileURL, "_blank");

    } catch (err) {
      console.error("Preview error:", err);
      alert("Preview failed");
    }
  };

  // =====================================================
  // DELETE
  // =====================================================
  const handleDelete = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?"))
      return;

    try {
      await axios.delete(
        `${API_URL}/api/assignments/documents/${documentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      loadDocuments();

    } catch (err) {
      console.error("Delete error:", err);
      alert("Delete failed");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024)
      return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">

      {/* ================= UPLOAD ================= */}
      <div className="bg-white border rounded-xl p-6">

        <h3 className="font-semibold mb-4">
          Upload Assignment Document
        </h3>

        <div className="grid md:grid-cols-3 gap-4">

          <Select
            options={documentTypes}
            value={selectedType}
            onChange={setSelectedType}
            placeholder="Document Type"
          />

          <input
            type="text"
            value={documentName}
            onChange={e => setDocumentName(e.target.value)}
            className="border rounded-xl px-3 py-2"
            placeholder="Document Name"
          />

          <input
            type="file"
            onChange={e => setFile(e.target.files[0])}
            className="border rounded-xl px-3 py-2"
          />

        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={handleUpload}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm"
          >
            {loading ? "Uploading..." : "Upload"}
          </button>
        </div>

      </div>

      {/* ================= LIST ================= */}
      <div className="bg-white border rounded-xl overflow-hidden">

        <table className="w-full text-sm">

          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Size</th>
              <th className="px-4 py-3 text-left">Uploaded</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>

          <tbody>
            {documents.map(doc => (
              <tr key={doc.DocumentID} className="border-t">
                <td className="px-4 py-3">{doc.TypeName}</td>
                <td className="px-4 py-3">{doc.DocumentName}</td>
                <td className="px-4 py-3">
                  {formatFileSize(doc.FileSize)}
                </td>
                <td className="px-4 py-3">
                  {new Date(doc.UploadedAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3">
  <div className="flex items-center gap-2">

    {/* Preview */}
    <button
      onClick={() => handlePreview(doc.DocumentID)}
      className="p-2 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 transition"
      title="Preview"
    >
      <FiEye size={16} />
    </button>

    {/* Download */}
    <button
      onClick={() =>
        handleDownload(doc.DocumentID, doc.DocumentName)
      }
      className="p-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition"
      title="Download"
    >
      <FiDownload size={16} />
    </button>

    {/* Delete */}
    <button
      onClick={() => handleDelete(doc.DocumentID)}
      className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition"
      title="Delete"
    >
      <FiTrash2 size={16} />
    </button>

  </div>
</td>


              </tr>
            ))}

            {documents.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center py-6 text-slate-400">
                  No documents uploaded yet.
                </td>
              </tr>
            )}

          </tbody>

        </table>

      </div>

    </div>
  );
};

export default AssignmentDocumentsTab;
